import { fetchCategoryCounts, searchMarket } from './marketCatalog';
import { marketDb } from './marketDb';
import { fetchDelivered, staffShipping } from './marketDelivered';
import { toSar } from './marketPricing';
import { EXCLUDE_SUB, MIX, MIX_BATCH, MIX_PER, PER_CAT, PICK_TABS, PICKS_HOME, PICKS_MIN, PICKS_POOL, SEARCHES,
  mixOrder, mixPools, riyadhDay, seededShuffle, windowIndex } from './homePicksCore';

// The rules (tabs, daily seed, «منوّع») are in homePicksCore.js, shared with the app.
export * from './homePicksCore';

// «اختيارات التجار» — filled AUTOMATICALLY (owner, 2026-11-02), no hand-picking.
// The one eligibility rule: the product has a real delivered price (shipping
// included) — a product whose shipping staff price («اطلب تثبيت سعر الشحن»)
// never shows. Products an admin took off the home (market_home_hidden,
// «أخفِ من الرئيسية») are left out; they stay in the market. The selection
// rotates DAILY: a seed from the Riyadh date, so everyone sees the same products
// all day (Growth OS / Clarity compare like with like) and they change the
// next day.
//
// The tabs (owner, 2026-11-02, second round — variety): «منوّع» first and
// selected, a mix across EVERY main category (round-robin, so no category
// dominates); then one tab per main market category, in the taxonomy's order.
// A category with fewer than PICKS_MIN listed products gets no tab; one whose
// eligible products for the day turn out fewer than PICKS_MIN when its tab is
// opened drops its tab then (the page shows «منوّع» again).
// The phones category is its ACCESSORIES (owner, 2026-11-02, first round): not
// the handsets (mobile_phones) and not spare parts / repair tools (phone_parts).

/**
 * A product for «اختيارات التجار» (owner, 2026-11-02): it has a PRODUCT price (any variant with a
 * price) and staff do not price its shipping. Not tied to the delivered-price switch — the cards
 * show the product price, and shipping is worked out in the cart.
 */
export function hasDeliveredPrice(p, info, dcfg, rates) {
  if (!info || staffShipping(info)) return false;
  return (info.skus || []).some((v) => toSar(v.price_cny, rates) > 0);
}

export async function fetchHiddenIds() {
  const { data, error } = await marketDb.from('market_home_hidden').select('product_id');
  if (error) return new Set();   // the table may not exist yet: nothing hidden
  return new Set((data || []).map((r) => r.product_id));
}
export const hideFromHome = async (productId) => { const r = await marketDb.from('market_home_hidden').insert({ product_id: productId }); memo = { day: null }; return r; };
export const showOnHome = async (productId) => { const r = await marketDb.from('market_home_hidden').delete().eq('product_id', productId); memo = { day: null }; return r; };
export async function isHiddenFromHome(productId) {
  const { data } = await marketDb.from('market_home_hidden').select('product_id').eq('product_id', productId).maybeSingle();
  return !!data;
}

// one quiet retry: a dropped request must not drop a whole tab (everyone sees the same tabs)
async function search(opts) {
  const r = await searchMarket(opts);
  if (!r.error) return r;
  await new Promise((res) => setTimeout(res, 600));
  const again = await searchMarket(opts);
  if (again.error) console.warn('[homePicks] market_search failed:', again.error.message || again.error);
  return again;
}

// at most SEARCHES category reads in flight
async function each(list, fn) {
  const out = new Array(list.length); let next = 0;
  await Promise.all(Array.from({ length: Math.min(SEARCHES, list.length) }, async () => {
    while (next < list.length) { const i = next++; out[i] = await fn(list[i]); }
  }));
  return out;
}

// One category's candidates for the day: a window of its list (the day picks
// which), `limit` of them — PER_CAT for its own tab, MIX_PER for «منوّع».
async function candidates(code, day, limit) {
  const w = windowIndex(code, day);
  let r = await search({ category: code, sort: 'newest', limit, offset: w * PER_CAT });
  if ((r.rows || []).length < limit / 2 && w > 0) r = await search({ category: code, sort: 'newest', limit, offset: 0 });
  const out = EXCLUDE_SUB[code] || [];
  return (r.rows || []).filter((p) => !out.includes(p.subcategory));
}

// Eligible products of each list, in the day's order of its category.
async function eligible(codes, lists, { dcfg, rates, day }) {
  const hidden = await hiddenIds();
  const rows = lists.map((l) => l.filter((p) => !hidden.has(p.id)));
  const map = await fetchDelivered(rows.flat().map((p) => p.id));
  return Object.fromEntries(codes.map((code, i) => [code,
    seededShuffle(rows[i].filter((p) => hasDeliveredPrice(p, map[p.id], dcfg, rates)), `${day}|${code}`).slice(0, PICKS_POOL)]));
}

// Speed (owner, 2026-11-02, second round): the page loads «منوّع» only — a few
// candidates from as many categories as it takes to fill it — and the tab list
// from the category counts; a category's own products load when its tab is
// tapped. Everything is kept for the day (memo), shared by the home and «عرض الكل».
let memo = { day: null, hidden: null, tabs: null, mix: new Map(), cat: new Map() };
const today = (day) => { if (memo.day !== day) memo = { day, hidden: null, tabs: null, mix: new Map(), cat: new Map() }; return memo; };
const keep = (map, key, make) => {
  if (!map.has(key)) { const pr = make(); map.set(key, pr); pr.catch(() => map.delete(key)); }
  return map.get(key);
};
function hiddenIds() { const m = memo; if (!m.hidden) { m.hidden = fetchHiddenIds(); } return m.hidden; }

/** The tabs for the day: «منوّع», then every category with at least PICKS_MIN listed products. */
export function dayTabs({ day = riyadhDay() } = {}) {
  const m = today(day);
  if (!m.tabs) {
    m.tabs = fetchCategoryCounts().then(({ counts, error }) => {
      const known = !error && counts && Object.keys(counts).length;
      return PICK_TABS.filter((t) => t.key === MIX || !known || counts[t.key]?.total >= PICKS_MIN);
    });
    m.tabs.catch(() => { m.tabs = null; });
  }
  return m.tabs;
}

/**
 * «منوّع» for the day, at least `size` products (8 on the home, PICKS_POOL on
 * «عرض الكل»): categories are read in the day's order, a batch at a time, until
 * `size` different categories have an eligible product — so the home's eight are
 * the first eight of «عرض الكل».
 */
export function mixPicks({ dcfg, rates, size = PICKS_HOME, day = riyadhDay() }) {
  const m = today(day);
  return keep(m.mix, size, async () => {
    const order = mixOrder(day);
    const pools = {};
    for (let at = 0; at < order.length;) {
      const enough = size >= PICKS_POOL ? false : order.filter((k) => (pools[k] || []).length).length >= size;
      if (enough) break;
      const batch = order.slice(at, at + (size >= PICKS_POOL ? order.length : MIX_BATCH)); at += batch.length;
      const lists = await each(batch, (code) => candidates(code, day, MIX_PER));
      Object.assign(pools, await eligible(batch, lists, { dcfg, rates, day }));
    }
    return mixPools(pools, order, size >= PICKS_POOL ? PICKS_POOL : size);
  });
}

/** One category's tab for the day (loaded when it is tapped, then kept). */
export function categoryPicks(code, { dcfg, rates, day = riyadhDay() }) {
  const m = today(day);
  return keep(m.cat, code, async () => (await eligible([code], [await candidates(code, day, PER_CAT)], { dcfg, rates, day }))[code]);
}
